/**
 * Renderer-agnostic camera rig. Angles are in sim terms: `yaw` is the direction the camera looks
 * across the ground (0 = +x, CCW positive, like unit facing); `pitch` is the downward tilt
 * (negative = looking up).
 *
 * Framing (pure helpers below, unit-tested in render.test.ts):
 *  - distance 3..70 yd. Far out it's the high three-quarter view the game was designed around
 *    (pivot at ground + 1.1); as it closes in the pivot rises toward the hero's head.
 *  - +atmos: pitch −29°..83° at every distance (WoW): pan the camera down to the horizon and on up
 *    toward the sky. As the pitch flattens the pivot rises to the head (you look out over the
 *    hero), and when the boom would dip into the ground the renderer slides the eye in along it
 *    (`boomGroundLimit`, toon/camCollision.ts) instead of blocking the pitch.
 *  - the wheel moves a target distance ~1 yd per notch near the hero and ~15 % per notch
 *    further out; the distance glides toward it (proportional easing with a minimum speed).
 *  - the horizontal focus is near-rigid on the hero (12 ms time constant); the renderer smooths
 *    the focus height over stairs (ground.ts smoothHeight).
 *  - optional "camera follow" (Never / Smart / Always): a forward / turn input edge starts a
 *    cosine-eased yaw return behind the character's facing (benilla camera.rs FollowRig).
 *  - camera collision (snap in, ease out) is applied by the renderer through `collideDistance`.
 */
import type { Vec2 } from '@keystone/sim';

export interface CameraState {
  yaw: number;
  pitch: number;
  /** Current (gliding) distance from the pivot, yards. */
  distance: number;
  /** Smoothed ground point the camera looks at. */
  focus: Vec2;
}

/** +atmos: `minPitch` (−29°, looking up) holds at every distance; the eye slides along the ground. */
export const CAMERA_LIMITS = { minDist: 3, maxDist: 70, minPitch: -0.5, maxPitch: 1.45 } as const;
/** +atmos: below this pitch (rad) the pivot starts rising to the head (a level look out over the hero) … */
export const LOW_LOOK_START = 0.5;
/** … and is fully at the head from here down. */
export const LOW_LOOK_END = 0.12;

/** Default distance (the telegraph-reading view). */
export const DEFAULT_DISTANCE = 30;
/** Pivot height above the ground at long range (the original top-down framing). */
export const FAR_PIVOT = 1.1;
/** Default head height (yd) when the renderer doesn't measure one. */
export const DEFAULT_HEAD = 1.9;
/** Below this distance the framing blends toward the close (head-height, free-pitch) view. */
export const CLOSE_BLEND_END = 22;
/** Keep the eye this far above the ground (the boom slides in along the ground below this). */
export const EYE_FLOOR_CLEARANCE = 0.5;
/** Zoom: 1 yd per notch below ZOOM_LINEAR_UNTIL, then ZOOM_RATIO per notch. */
export const ZOOM_RATIO = 1.15;
export const ZOOM_LINEAR_UNTIL = 1 / (ZOOM_RATIO - 1); // ≈ 6.67: the two rules meet at 1 yd/notch
/** Wheel deltaY per notch (Chrome / Firefox line mode both normalize to ~100 px). */
export const WHEEL_NOTCH = 100;
/** Horizontal focus time constant (ms): near-rigid (benilla: rigid). */
export const FOCUS_TAU_MS = 12;
/** Collision: snap in, then ease back out at this rate (1/s) (benilla CAM_RETURN_RATE). */
export const COLLIDE_RETURN_RATE = 6;
/** Base FOV (degrees) and the extra width at the closest zoom. */
export const BASE_FOV = 42;
export const CLOSE_FOV_BONUS = 8;

export type CameraFollowMode = 'never' | 'smart' | 'always';
export const CAMERA_FOLLOW_MODES: readonly CameraFollowMode[] = ['never', 'smart', 'always'];

/** Input word bits for the follow return (see input/cameraSignals.ts). */
export const FOLLOW_BITS = { forward: 1, back: 2, strafeLeft: 4, strafeRight: 8, turnLeft: 16, turnRight: 32, autorun: 64 } as const;
const MOVE_BITS = FOLLOW_BITS.forward | FOLLOW_BITS.back | FOLLOW_BITS.autorun;
const STRAFE_BITS = FOLLOW_BITS.strafeLeft | FOLLOW_BITS.strafeRight;
const TURN_BITS = FOLLOW_BITS.turnLeft | FOLLOW_BITS.turnRight;
const ANY_MOVE = MOVE_BITS | STRAFE_BITS | TURN_BITS;
/** Inputs that may swing the camera behind the hero: forward / autorun / turning. */
const DRIVE_BITS = FOLLOW_BITS.forward | FOLLOW_BITS.autorun | TURN_BITS;

/** What the input layer reports to the follow return (read each frame; see input/cameraSignals.ts). */
export interface FollowSignals {
  /** Held movement inputs as FOLLOW_BITS. */
  moveWord: number;
  /** The user is holding a camera drag / mouselook button. */
  lookHeld: boolean;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const wrap = (a: number): number => Math.atan2(Math.sin(a), Math.cos(a));
const smoothstep = (t: number): number => {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
};

/** 0 at the closest zoom … 1 from CLOSE_BLEND_END out (the far, top-down framing). */
export function farBlend(distance: number): number {
  return smoothstep((distance - CAMERA_LIMITS.minDist) / (CLOSE_BLEND_END - CAMERA_LIMITS.minDist));
}

/** +atmos: 0 at the usual high pitches … 1 when looking level / up (the pivot rises to the head). */
export function lowLook(pitch: number): number {
  return 1 - smoothstep((pitch - LOW_LOOK_END) / (LOW_LOOK_START - LOW_LOOK_END));
}

/** Pivot height above the ground: head height close in (or looking level), ground + 1.1 far out. */
export function pivotHeightAt(distance: number, head = DEFAULT_HEAD, pitch: number = LOW_LOOK_START): number {
  return head + (FAR_PIVOT - head) * farBlend(distance) * (1 - lowLook(pitch));
}

/** Lowest allowed pitch (the same at every distance: the renderer slides the eye along the ground). */
export function minPitchAt(_distance?: number): number {
  return CAMERA_LIMITS.minPitch;
}

/**
 * +atmos: how far the boom reaches over flat ground before the eye would dip below `clearance`
 * (Infinity when it never does): the WoW slide. `pivotH` is the pivot's height above the ground.
 */
export function flatBoomLimit(pivotH: number, pitch: number, clearance = EYE_FLOOR_CLEARANCE): number {
  const s = Math.sin(pitch);
  if (s >= -1e-6) return Infinity;
  return Math.max(0, (pivotH - clearance) / -s);
}

/** Vertical FOV (degrees) at a distance: slightly wider close in. */
export function fovAt(distance: number): number {
  return BASE_FOV + CLOSE_FOV_BONUS * (1 - farBlend(distance));
}

/** Distance → notch coordinate (linear 1 yd/notch close in, logarithmic further out). */
function toNotch(d: number): number {
  return d <= ZOOM_LINEAR_UNTIL ? d - ZOOM_LINEAR_UNTIL : Math.log(d / ZOOM_LINEAR_UNTIL) / Math.log(ZOOM_RATIO);
}
function fromNotch(u: number): number {
  return u <= 0 ? u + ZOOM_LINEAR_UNTIL : ZOOM_LINEAR_UNTIL * Math.pow(ZOOM_RATIO, u);
}

/** The new target distance after `notches` wheel notches (positive = zoom out), clamped. */
export function zoomStep(target: number, notches: number): number {
  return clamp(fromNotch(toNotch(target) + notches), CAMERA_LIMITS.minDist, CAMERA_LIMITS.maxDist);
}

/** One frame of the distance glide: proportional easing (10/s) with a 2 yd/s floor so it lands. */
export function glideDistance(current: number, target: number, dtMs: number): number {
  const diff = target - current;
  if (dtMs <= 0 || Math.abs(diff) < 1e-3) return dtMs <= 0 ? current : target;
  const dt = dtMs / 1000;
  const step = Math.max(Math.abs(diff) * (1 - Math.exp(-10 * dt)), 2 * dt);
  return Math.abs(diff) <= step ? target : current + Math.sign(diff) * step;
}

/** Collision distance: snaps in to `open` when geometry intrudes, eases back out at 6/s. */
export function collideDistance(current: number, open: number, dtMs: number): number {
  if (!Number.isFinite(current) || open <= current || dtMs <= 0) return open;
  return current + (open - current) * (1 - Math.exp(-COLLIDE_RETURN_RATE * (dtMs / 1000)));
}

/** Duration (s) of a follow return over `delta` radians: 180°/s, clamped 0.1..2 s. */
export function followDuration(delta: number): number {
  return clamp(Math.abs(delta) / Math.PI, 0.1, 2);
}

/**
 * The camera-follow return (benilla camera.rs FollowRig, 1.12 cameraSmoothStyle): an input edge
 * snapshots a return from the current yaw offset to directly behind the character, which then
 * plays out on its own (holding W changes nothing). A held camera drag cancels it.
 */
export class FollowReturn {
  private lastWord: number | null = null;
  private arm: { from: number; dur: number; t: number } | null = null;

  get active(): boolean {
    return this.arm !== null;
  }

  cancel(): void {
    this.arm = null;
  }

  /** Returns the camera yaw to use this frame, or null to leave it alone. */
  advance(mode: CameraFollowMode, sig: FollowSignals, camYaw: number, faceYaw: number, dtS: number): number | null {
    const word = sig.moveWord;
    const prev = this.lastWord;
    this.lastWord = word;
    if (mode === 'never') {
      this.arm = null;
      return null;
    }
    if (sig.lookHeld) {
      this.arm = null;
      return null;
    }
    if (prev !== null && prev !== word) {
      const driving = (word & DRIVE_BITS) !== 0;
      // Strafing / backpedalling alone never swings the camera behind the hero (it keeps its facing).
      const lateral = !driving && (word & ANY_MOVE) !== 0;
      // Smart: only edges into a forward / turning state return; standing, stopping or strafing cancels.
      if (!lateral && (mode === 'always' || driving)) {
        const from = wrap(camYaw - faceYaw);
        this.arm = Math.abs(from) < 1e-3 ? null : { from, dur: followDuration(from), t: 0 };
      } else this.arm = null;
    }
    const a = this.arm;
    if (!a) return null;
    a.t += dtS;
    const s = a.t / a.dur;
    if (s >= 1) {
      this.arm = null;
      return wrap(faceYaw);
    }
    const e = (1 - Math.cos(Math.PI * s)) * 0.5;
    return wrap(faceYaw + a.from * (1 - e));
  }
}

export class CameraRig {
  state: CameraState = { yaw: Math.PI / 2, pitch: 0.95, distance: DEFAULT_DISTANCE, focus: { x: 0, y: 0 } };
  /** Wheel target; null = settled at `state.distance` (so direct writes to it just work). */
  private zoomTarget: number | null = null;
  /** Head height of the followed hero (the renderer measures it; pivot / min pitch use it). */
  headHeight = DEFAULT_HEAD;
  /** Camera follow style (settings). */
  followMode: CameraFollowMode = 'never';
  readonly followReturn = new FollowReturn();
  /** Called when a wheel zoom settles (persist it). */
  onZoomSettled: ((distance: number) => void) | null = null;
  /** performance-ish clock of the last rotate() (a drag counts as "look held" for a moment). */
  private rotatedAgoMs = Infinity;

  /** The distance the wheel is heading to (or the current one). */
  get targetDistance(): number {
    return this.zoomTarget ?? this.state.distance;
  }

  /** Jump to a distance (no glide), e.g. a restored setting. */
  setDistance(d: number): void {
    this.state.distance = clamp(d, CAMERA_LIMITS.minDist, CAMERA_LIMITS.maxDist);
    this.zoomTarget = null;
    this.clampPitch();
  }

  /** Mouse drag in pixels → yaw/pitch. */
  rotate(dxPx: number, dyPx: number): void {
    this.state.yaw = wrap(this.state.yaw - dxPx * 0.006);
    this.state.pitch = clamp(this.state.pitch + dyPx * 0.004, minPitchAt(this.state.distance), CAMERA_LIMITS.maxPitch);
    this.rotatedAgoMs = 0;
    this.followReturn.cancel();
  }

  /** Wheel delta (positive = zoom out; ~100 per notch). Sets the target; `follow` glides to it. */
  zoom(delta: number): void {
    if (!Number.isFinite(delta) || delta === 0) return;
    this.zoomTarget = zoomStep(this.targetDistance, delta / WHEEL_NOTCH);
  }

  /** Pivot height above the ground for the current distance and pitch. */
  pivotHeight(): number {
    return pivotHeightAt(this.state.distance, this.headHeight, this.state.pitch);
  }

  /** Lowest pitch allowed at the current distance. */
  minPitch(): number {
    return minPitchAt(this.state.distance);
  }

  /** Vertical FOV for the current distance. */
  fov(): number {
    return fovAt(this.state.distance);
  }

  /**
   * Per frame: follow `target` (near-rigid; snaps when far away: teleports, wipes), glide the
   * zoom, and run the follow return when `facing` / `signals` are given.
   */
  follow(target: Vec2, dtMs: number, facing?: number, signals?: FollowSignals): void {
    const f = this.state.focus;
    const d = Math.hypot(target.x - f.x, target.y - f.y);
    if (d > 30 || dtMs <= 0) {
      f.x = target.x;
      f.y = target.y;
    } else {
      const k = 1 - Math.exp(-dtMs / FOCUS_TAU_MS);
      f.x += (target.x - f.x) * k;
      f.y += (target.y - f.y) * k;
    }
    this.update(dtMs, facing, signals);
  }

  /** Zoom glide + pitch clamp + follow return (called by `follow`; call directly with no target). */
  update(dtMs: number, facing?: number, signals?: FollowSignals): void {
    const dt = Math.max(0, dtMs);
    if (this.zoomTarget !== null) {
      this.state.distance = glideDistance(this.state.distance, this.zoomTarget, dt);
      if (this.state.distance === this.zoomTarget) {
        this.zoomTarget = null;
        this.onZoomSettled?.(this.state.distance);
      }
    }
    this.clampPitch();
    this.rotatedAgoMs += dt;
    if (facing !== undefined && signals) {
      const sig: FollowSignals = signals.lookHeld || this.rotatedAgoMs > 150 ? signals : { moveWord: signals.moveWord, lookHeld: true };
      const yaw = this.followReturn.advance(this.followMode, sig, this.state.yaw, facing, dt / 1000);
      if (yaw !== null) this.state.yaw = yaw;
    }
  }

  private clampPitch(): void {
    this.state.pitch = clamp(this.state.pitch, this.minPitch(), CAMERA_LIMITS.maxPitch);
  }

  /** Camera eye position (before collision; slid in over flat ground): ground offset (sim x/y) plus height above the pivot's ground. */
  eye(): { x: number; y: number; height: number } {
    const { yaw, pitch, focus } = this.state;
    const distance = Math.min(this.state.distance, flatBoomLimit(this.pivotHeight(), pitch));
    const horiz = distance * Math.cos(pitch);
    return { x: focus.x - Math.cos(yaw) * horiz, y: focus.y - Math.sin(yaw) * horiz, height: this.pivotHeight() + distance * Math.sin(pitch) };
  }
}
